/**
 * Shared webhook code examples panel — shows curl/python/js snippets
 * with language tabs and copy-to-clipboard. Used in both the mate
 * webhook popover and task webhook section.
 */
import { Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type Lang = "curl" | "python" | "js";

function buildExamples(url: string, bodyJson: string): Record<Lang, string> {
	return {
		curl: `curl -X POST ${url} \\\n  -H "Content-Type: application/json" \\\n  -d '${bodyJson}'`,
		python: `import requests\n\nrequests.post(\n    "${url}",\n    json=${bodyJson},\n)`,
		js: `fetch("${url}", {\n  method: "POST",\n  headers: { "Content-Type": "application/json" },\n  body: JSON.stringify(${bodyJson}),\n});`,
	};
}

interface WebhookCodeExamplesProps {
	/** Full or masked webhook URL to show in examples. */
	url: string;
	/** JSON body string for examples (default: task-style payload). */
	bodyJson?: string;
}

export function WebhookCodeExamples({
	url,
	bodyJson = '{"message": "Your task here"}',
}: WebhookCodeExamplesProps) {
	const [lang, setLang] = useState<Lang>("curl");
	const examples = buildExamples(url, bodyJson);

	return (
		<div className="space-y-1.5">
			<div className="flex items-center justify-between">
				<p className="text-xs font-medium text-muted-foreground">Usage</p>
				<div className="flex gap-0.5">
					{(["curl", "python", "js"] as const).map((l) => (
						<button
							key={l}
							type="button"
							className={cn(
								"px-2 py-0.5 text-[11px] rounded-md transition-colors",
								lang === l ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground"
							)}
							onClick={() => setLang(l)}
						>
							{l === "js" ? "JS" : l === "python" ? "Python" : "curl"}
						</button>
					))}
				</div>
			</div>
			<div className="relative">
				<pre className="rounded-md border bg-muted/50 px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap break-all">
					{examples[lang]}
				</pre>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="absolute top-1 right-1 h-6 w-6"
							onClick={() => {
								navigator.clipboard.writeText(examples[lang]);
								toast.success("Example copied");
							}}
						>
							<Copy className="h-3 w-3" />
							<span className="sr-only">Copy example</span>
						</Button>
					</TooltipTrigger>
					<TooltipContent>Copy example</TooltipContent>
				</Tooltip>
			</div>
		</div>
	);
}
